import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { useSnapshot } from 'valtio'
import { RadarInitializer } from '../../App'
import { state } from '../../lib/state'
import { AdminNavigation } from './AdminNavigation'

// Add Featurebase type to window
declare global {
  interface Window {
    Featurebase?: (
      ...args: [string, Record<string, unknown>?] | [string, string]
    ) => void
  }
}

export const AdminLayout = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { availableRadars, currentRadarId, context, userEmail } =
    useSnapshot(state)

  // Initialize Featurebase Messenger
  useEffect(() => {
    const siteUrl =
      (context?.siteUrl &&
        RegExp(/https:\/\/([^.]+)\.atlassian\.net/).exec(
          context?.siteUrl || ''
        )?.[1]) ||
      context?.siteUrl ||
      ''

    if (typeof window.Featurebase === 'function') {
      const bootConfig: Record<string, string> = {
        appId: '67edeb58b4221952a834d59c',
        theme: 'dark',
        language: 'en',
        cloudId: context?.cloudId || '',
        siteUrl: siteUrl,
        userId: context?.accountId || ''
      }
      if (userEmail) {
        bootConfig.email = userEmail
      }
      window.Featurebase('boot', bootConfig)
    }
  }, [context?.siteUrl, context?.cloudId, context?.accountId, userEmail])

  console.info('AdminLayout', 'Rendering', {
    pathname: location.pathname,
    availableRadarsCount: availableRadars?.length,
    currentRadarId
  })

  useEffect(() => {
    // Pages that don't require a radar selection
    const pagesWithoutRadarRequirement = [
      '/admin/radar-list',
      '/admin/billing',
      '/admin'
    ]

    if (pagesWithoutRadarRequirement.includes(location.pathname)) {
      console.info('AdminLayout', 'Page does not require radar selection')
      return
    }

    // If no radars exist, redirect to radar-list
    if (!availableRadars || availableRadars.length === 0) {
      console.info(
        'AdminLayout',
        'No radars available, redirecting to radar-list'
      )
      navigate('/admin/radar-list')
      return
    }

    // If no radar is selected, select the first available radar
    if (!currentRadarId) {
      console.info('AdminLayout', 'No radar selected, selecting first radar', {
        firstRadarId: availableRadars[0].id
      })
      state.currentRadarId = availableRadars[0].id
      return
    }

    // If current radar doesn't exist in available radars, select the first one
    const currentRadarExists = availableRadars.some(
      radar => radar.id === currentRadarId
    )
    if (!currentRadarExists) {
      console.info(
        'AdminLayout',
        'Current radar does not exist, selecting first radar',
        {
          currentRadarId,
          firstRadarId: availableRadars[0].id
        }
      )
      state.currentRadarId = availableRadars[0].id
    }
  }, [location.pathname, availableRadars, currentRadarId, navigate])

  return (
    <div className="flex flex-col h-screen bg-[var(--ds-surface)]">
      <header
        className="sticky top-0 z-10 bg-[var(--ds-surface)] border-b border-[var(--ds-border)]"
        data-testid="admin-header"
      >
        <AdminNavigation />
      </header>
      <main
        className="flex-1 overflow-auto theme-aware-bg"
        data-testid="admin-main-content"
      >
        <div className="px-4">
          <RadarInitializer />
        </div>
      </main>
    </div>
  )
}
